// How a game is classified for the filter, and for every count that must
// equal the filtered library's (the dashboard's): the owner's side and
// outcome, the time class, the rating band, how it ended.
import { SIMILAR_RATING, type FilterableGame, type GameColour, type GameEnding, type GameOutcome, type RatingBand, type TimeClass } from '@chess/api-model';

/** The colour the owner played; undefined when the owner matches neither or both. */
export function ownerColour(game: Pick<FilterableGame, 'owner' | 'white' | 'black'>): GameColour | undefined {
  const owner = game.owner.trim().toLowerCase();
  const white = game.white.name.trim().toLowerCase() === owner;
  const black = game.black.name.trim().toLowerCase() === owner;
  if (white === black) return undefined;
  return white ? 'white' : 'black';
}

/** The owner's outcome from the result; undefined for an unfinished game or no owner side. */
export function ownerOutcome(game: Pick<FilterableGame, 'owner' | 'white' | 'black' | 'result'>): GameOutcome | undefined {
  const colour = ownerColour(game);
  if (!colour || game.result === '*') return undefined;
  if (game.result === '1/2-1/2') return 'draw';
  const whiteWon = game.result === '1-0';
  return (colour === 'white') === whiteWon ? 'win' : 'loss';
}

/**
 * Chess.com's live classes by expected duration, base plus forty increments.
 * `timeControl` is the stored form: "5+0" (minutes), "30s+0" (seconds) or "daily".
 */
export function timeClassOf(timeControl?: string): TimeClass | undefined {
  if (!timeControl) return undefined;
  if (timeControl === 'daily') return 'daily';
  const [base, inc = '0'] = timeControl.split('+');
  const baseSeconds = base.endsWith('s') ? Number(base.slice(0, -1)) : Number(base) * 60;
  if (!Number.isFinite(baseSeconds)) return undefined;
  const seconds = baseSeconds + 40 * Number(inc || 0);
  if (seconds < 180) return 'bullet';
  if (seconds < 600) return 'blitz';
  return 'rapid';
}

/** The PGN's headers as a map. */
export function pgnHeaders(pgn: string): Record<string, string> {
  const headers: Record<string, string> = {};
  const pattern = /^\[(\w+)\s+"((?:[^"\\]|\\.)*)"\]\s*$/gm;
  for (let m = pattern.exec(pgn); m; m = pattern.exec(pgn)) headers[m[1]] = m[2];
  return headers;
}

/** How the game ended, from chess.com's Termination header; undefined when it says nothing known. */
export function endingOf(pgn: string): GameEnding | undefined {
  const t = (pgnHeaders(pgn).Termination ?? '').toLowerCase();
  if (!t) return undefined;
  if (t.includes('checkmate')) return 'checkmate';
  if (t.includes('resignation')) return 'resignation';
  if (t.includes('on time') || t.includes('timeout')) return 'time';
  if (t.includes('abandon')) return 'abandoned';
  if (t.includes('drawn') || t.includes('draw') || t.includes('stalemate') || t.includes('repetition') || t.includes('agreement')) return 'draw';
  return undefined;
}

/** The opponent's rating against the owner's; undefined without both ratings or an owner side. */
export function ratingBandOf(game: Pick<FilterableGame, 'owner' | 'white' | 'black'>): RatingBand | undefined {
  const colour = ownerColour(game);
  if (!colour) return undefined;
  const mine = colour === 'white' ? game.white.rating : game.black.rating;
  const theirs = colour === 'white' ? game.black.rating : game.white.rating;
  if (mine === undefined || theirs === undefined) return undefined;
  if (theirs - mine > SIMILAR_RATING) return 'stronger';
  if (mine - theirs > SIMILAR_RATING) return 'weaker';
  return 'similar';
}
